import { AsyncPipe, DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { getEntityType } from '@datorama/akita';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { UserDialogComponent, UserDialogSubmit } from '@features/user-dialog/user-dialog.component';

import { ButtonComponent } from '@shared/components/button/button.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { GroupByPipe } from '@shared/pipes/group-by.pipe';
import { DepartmentType, PermissionType } from '@shared/services/gql.service';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { MainQuery } from '@shared/store/main/main.query';
import { MainService } from '@shared/store/main/main.service';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { sortByUserName, TrialUserQuery } from '@shared/store/trial-users/trial-user.query';
import { TrialUserService } from '@shared/store/trial-users/trial-user.service';
import { TrialUserState, UserModel } from '@shared/store/trial-users/trial-user.store';
import { Utils } from '@shared/utils/utils';

import { injectCopyUsersModal } from './components/copy-users-modal.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    InputComponent,
    FormsModule,
    ButtonComponent,
    TooltipDirective,
    GroupByPipe,
    NgClass,
    IconComponent,
    DatePipe,
  ],
  selector: 'aux-users',
  templateUrl: './users.component.html',
})
export class UsersComponent {
  public authService = inject(AuthService);
  public trialUserQuery = inject(TrialUserQuery);

  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private mainQuery = inject(MainQuery);
  private mainService = inject(MainService);
  private overlayService = inject(OverlayService);
  private trialUserService = inject(TrialUserService);
  private vendorsQuery = inject(OrganizationQuery);

  flags = injectFlags({ showUserLastLogin: 'user_last_login' });
  showCopyUsersModal = injectCopyUsersModal();

  copyUserPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_COPY_USERS_AND_PERMISSIONS],
  });

  copyUserPermissionDisabledTooltip = computed(() => {
    return this.copyUserPermission() ? '' : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  });

  showAuxSupport = false;

  loading$ = combineLatest([
    this.trialUserQuery.selectLoading(),
    this.vendorsQuery.selectLoading(),
  ]).pipe(
    map(([users, vendors]) => {
      this.showAuxSupport = false;

      return users || vendors;
    })
  );

  auxUsers$?: Observable<getEntityType<TrialUserState>[]>;
  filterValue = '';
  userHasUpdateUserPermission = false;
  users$?: Observable<getEntityType<TrialUserState>[]>;

  private _searchSubject: BehaviorSubject<string> = new BehaviorSubject('');

  constructor() {
    this.setUserPermissions();
    this.setUserLists();
    this.subscribeToSearch();
  }

  filterChanges(): void {
    if (this.filterValue !== this._searchSubject.getValue()) {
      this.setUserLists();
    }
  }

  private filterUserByText = (user: getEntityType<TrialUserState>) => {
    const comparedValue = this.filterValue.toLowerCase();

    return (
      (user.given_name && user.given_name.toLowerCase().includes(comparedValue)) ||
      (user.family_name && user.family_name.toLowerCase().includes(comparedValue)) ||
      (user.title && user.title.toLowerCase().includes(comparedValue)) ||
      !!(user.email && user.email.toLowerCase().includes(comparedValue))
    );
  };

  getDepartmentString(departments: (DepartmentType | null)[] | null | undefined) {
    if (departments) {
      return departments
        .map((x) => {
          if (x) {
            return Utils.DEPARTMENT_OPTIONS[x];
          }

          return '';
        })
        .join(', ');
    }

    return '';
  }

  noPermissionsTooltipText(): string {
    return this.userHasUpdateUserPermission
      ? ''
      : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  }

  async onAddUser() {
    const ref = this.overlayService.openPopup<
      { formValue?: unknown; mode: 'add' | 'edit' },
      UserDialogSubmit
    >({ data: { mode: 'add' }, modal: UserDialogComponent, settings: { header: 'Add User' } });

    const { data } = await firstValueFrom(ref.afterClosed$);

    if (data) {
      const { department, email, family_name, given_name, role, title } = data;

      await this.trialUserService.add({
        department,
        email,
        family_name,
        given_name,
        role,
        title,
        trial_id: this.mainQuery.getValue().trialKey,
      });

      await firstValueFrom(this.mainService.getUserList$());
    }
  }

  async onEditUser(user: UserModel) {
    let user_organization_id = user.organization?.id;

    if (user.email?.includes('@auxili.us')) {
      user_organization_id = 'Aux Support';
    }

    const ref = this.overlayService.openPopup<
      { formValue?: unknown; mode: 'add' | 'edit' },
      UserDialogSubmit
    >({
      data: { formValue: { ...user, organization_id: user_organization_id }, mode: 'edit' },
      modal: UserDialogComponent,
      settings: { header: 'Edit User', primaryButton: { label: 'Save Edits' } },
    });

    const { data } = await firstValueFrom(ref.afterClosed$);

    if (data) {
      const { department, email, family_name, given_name, role, title } = data;

      const { success } = await this.trialUserService.update({
        department,
        email,
        family_name,
        given_name,
        role,
        sub: user.sub as string,
        title,
      });

      if (success) {
        this.overlayService.success('User updated successfully!');
      }
    }
  }

  onRemoveUser(user: UserModel) {
    this.overlayService.openPopup<
      { message: string },
      boolean | undefined,
      ConfirmationModalComponent
    >({
      content: ConfirmationModalComponent,
      data: {
        message: `Are you sure that you want to remove ${user.given_name} ${user.family_name}?`,
      },
      settings: {
        header: 'Remove User',
        primaryButton: {
          action: async (instance) => {
            await this.trialUserService.remove(user.sub as string);
            instance?.ref.close();
          },
          label: 'Remove',
        },
      },
    });
  }

  async resendInvitation(user: UserModel) {
    const { email } = user;

    if (email) {
      await this.trialUserService.reinvite(email);
    }
  }

  private setAuxUsers$(): void {
    this.auxUsers$ = this.trialUserQuery.selectAll({
      filterBy: [
        (user) => (user.email ? user.email.includes('@auxili.us') : false),
        this.filterUserByText,
      ],
      sortBy: sortByUserName,
    });
  }

  private setUserLists(): void {
    this.setUsers$();
    this.setAuxUsers$();
  }

  private setUserPermissions(): void {
    combineLatest([
      this.authService.isAuthorized$({
        permissions: [PermissionType.PERMISSION_UPDATE_USERS],
        sysAdminsOnly: false,
      }),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([userHasUpdateUserPermission]) => {
        this.userHasUpdateUserPermission = userHasUpdateUserPermission;
      });
  }

  private setUsers$(): void {
    this.users$ = this.trialUserQuery.selectAll({
      filterBy: [
        (user) => (user.email ? !user.email.includes('@auxili.us') : true),
        this.filterUserByText,
      ],
      sortBy: sortByUserName,
    });
  }

  private subscribeToSearch(): void {
    this._searchSubject
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
      .subscribe((searchText) => {
        if (searchText.length > 2 || searchText.length === 0) {
          this.setUserLists();
          this.changeDetector.detectChanges();
        }
      });
  }

  toggleAuxSupport() {
    this.showAuxSupport = !this.showAuxSupport;
  }

  updateSearch(target: EventTarget | null): void {
    const searchText = target as HTMLInputElement;

    if (searchText.value.length > 2 || searchText.value.length === 0) {
      this._searchSubject.next(this.filterValue);
    }
  }
}
